import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Card, CardBody, Flex, Spinner, Text, Button, HStack, useDisclosure } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useParentTicketStore, type ParentTicketWithMeta } from '@/store/parentTicket'
import type { ParentTicket, TicketUpdate } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import { useParentTicketDetailForm } from '@/hooks/useParentTicketDetailForm'
import { PageHeader } from '@/components/common/PageHeader'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

import StoreDetailsCard from '@/components/helpdesk/TicketDetail/StoreDetailsCard'
import Timeline, { type TimelineItem } from '@/components/helpdesk/TicketDetail/Timeline'
import TicketProperties from '@/components/helpdesk/TicketDetail/TicketProperties'
import StatusSlaCard from '@/components/helpdesk/TicketDetail/StatusSlaCard'
import Assignment from '@/components/helpdesk/TicketDetail/Assignment'
import TicketUpdateModal from '@/components/helpdesk/TicketDetail/TicketUpdateModal'

type Option = { label: string; value: string }



/**
 * View-only page for Parent Tickets
 * This page displays ticket details in read-only mode using actual data from the database
 */
export default function ParentTicketViewPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const getTicketById = useParentTicketStore((s) => s.getTicketById)
  const mergeTicket = useParentTicketStore((s) => s.mergeTicket)
  const ticket = id ? getTicketById(id) : undefined
  const meta = ticket as ParentTicketWithMeta | undefined
  const hasFullRequest = Boolean(meta?.request)
  const hasFullStore = Boolean(meta?.store) || !(meta?.store_id != null || (meta?.store_code != null && meta?.store_code !== ''))
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [statusOptions, setStatusOptions] = useState<Option[]>([])
  const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure()
  const { showSuccess, showError } = useToastNotification()
  const [ticketUpdates, setTicketUpdates] = useState<TicketUpdate[]>([])
  const [editingUpdate, setEditingUpdate] = useState<TicketUpdate | null>(null)
  const propertiesRef = useRef<HTMLDivElement>(null)
  const [propertiesHeight, setPropertiesHeight] = useState<number | null>(null)

  const fetchTicketUpdates = useCallback(async () => {
    if (!id) return
    const res = await TicketService.getTicketUpdates(id)
    if (res.success && res.data) {
      setTicketUpdates(res.data.data ?? [])
    }
  }, [id])

  // Get form values from ticket data
  const { form, setField } = useParentTicketDetailForm(ticket)
  const statusLabel = useMemo(() => form.status, [form.status])

  useEffect(() => {
    if (!id) {
      navigate('/tickets', { replace: true })
    }
  }, [id, navigate])

  // Fetch status options from ticket_statuses table
  useEffect(() => {
    let cancelled = false
    apiClient.get<{ data: Array<{ id: number; code: string; label?: string | null }> }>(
      '/service-catalog/parent-ticket-statuses',
      { params: { per_page: 100 } }
    ).then((res) => {
      if (cancelled) return
      const items = res.data?.data
      if (Array.isArray(items)) {
        setStatusOptions(items.map((s) => ({
          label: s.label || s.code,
          value: s.code,
        })))
      }
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Fetch ticket updates
  useEffect(() => {
    fetchTicketUpdates()
  }, [fetchTicketUpdates])

  // Measure TicketProperties height so right column (StatusSlaCard + Timeline) can align and Timeline can scroll
  useEffect(() => {
    const el = propertiesRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry?.contentRect?.height) setPropertiesHeight(entry.contentRect.height)
    })
    ro.observe(el)
    setPropertiesHeight(el.getBoundingClientRect().height)
    return () => ro.disconnect()
  }, [ticket])

  // Fetch full ticket when not in store or when missing request or store details (e.g. from list API)
  useEffect(() => {
    if (!id) return
    if (ticket != null && hasFullRequest && hasFullStore) return
    let cancelled = false
    setFetchError(null)
    setFetching(true)
    TicketService.getParentTicket(id)
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) {
          mergeTicket(res.data)
        } else {
          setFetchError(res.message ?? 'Failed to load ticket')
        }
      })
      .catch(() => {
        if (!cancelled) setFetchError('Failed to load ticket')
      })
      .finally(() => {
        if (!cancelled) setFetching(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, ticket, hasFullRequest, hasFullStore, mergeTicket])

  const formatTimelineDate = (iso: string) =>
    new Date(iso).toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })

  const timelineItems: TimelineItem[] = useMemo(() => {
    const items: TimelineItem[] = []

    for (const u of ticketUpdates) {
      items.push({
        id: `update-${u.id}`,
        actor: u.author?.full_name ?? 'Agent',
        action: u.content,
        timeLabel: formatTimelineDate(u.createdAt ?? u.created_at),
        type: 'update',
        onEdit: () => setEditingUpdate(u),
      })
    }

    // Sort by date descending (most recent first)
    items.sort((a, b) => {
      const da = a.timeLabel ? new Date(a.timeLabel).getTime() : 0
      const db = b.timeLabel ? new Date(b.timeLabel).getTime() : 0
      return db - da
    })

    return items
  }, [ticket, ticketUpdates])

  if (!id) {
    return null
  }

  if (fetching) {
    return (
      <Box p={4} display="flex" alignItems="center" justifyContent="center" minH="200px">
        <Spinner size="lg" />
        <Text ml={3}>Loading ticket...</Text>
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Box p={4}>
        <Card>
          <CardBody>
            <Text color="gray.600">{fetchError}</Text>
            <Text
              as="button"
              color="blue.600"
              mt={2}
              onClick={() => navigate('/tickets')}
            >
              Back to list
            </Text>
          </CardBody>
        </Card>
      </Box>
    )
  }

  if (!ticket) {
    return (
      <Box p={4}>
        <Card>
          <CardBody>
            <Text color="gray.600">Parent ticket not found.</Text>
            <Text
              as="button"
              color="blue.600"
              mt={2}
              onClick={() => navigate('/tickets')}
            >
              Back to list
            </Text>
          </CardBody>
        </Card>
      </Box>
    )
  }

  // Get store and request data from meta
  const storeData = meta?.store
  const requestData = meta?.request
  
  // Get store_code from meta or mapped ticket (mapApiRowToParentTicket includes store_code)
  const ticketWithStoreCode = ticket as typeof ticket & { store_code?: string }
  const storeCode = meta?.store_code || ticketWithStoreCode.store_code || storeData?.storeCode

  return (
    <Box
      bg={colors.bg}
      p={6}
      w="100%"
      minW={0}
      minH="calc(100vh - 80px - 48px)"
      display="flex"
      flexDir="column"
    >
      {/* Read-only Header */}
      <PageHeader
        mb={4}
        start={
          <Button
            variant="ghost"
            leftIcon={<ChevronLeft size={18} />}
            onClick={() => navigate('/tickets')}
            px={2}
          >
            Go Back
          </Button>
        }
        end={<></>}
      />

      <Flex gap={6} align="stretch" minW={0} flex="1" minH={0}>
        {/* ================= LEFT ================= */}
        <Box
          w="300px"
          flexShrink={0}
          display="flex"
          flexDir="column"
          gap={4}
          minH={0}
        >
          <StoreDetailsCard
            businessUnitName={storeData?.businessUnitName}
            storeName={storeData?.storeName}
            branchCode={storeCode || storeData?.storeCode || storeData?.branchCode}
            contactPerson={storeData?.contactPerson}
            email={storeData?.storeEmail}
            contactNumber={storeData?.storeContact || storeData?.contactNumber}
            storeAddress={storeData?.branchAddress || storeData?.locationAddress}
          />

          <Assignment
            agent={meta?.agent}
            primaryEngineer={meta?.primaryEngineer}
            backupEngineer={meta?.backupEngineer}
            isReadOnly={true}
          />
        </Box>

        {/* ================= MIDDLE ================= */}
        <Box flex="1" minW={0} display="flex" flexDir="column" gap={4} minH={0}>
          <Box ref={propertiesRef}>
            <TicketProperties
              request={requestData}
              ticket={{
              ticketNumber: (meta as any)?.ticket_number ?? undefined,
              description: ticket.description,
              notes: (meta as any)?.notes ?? undefined,
              workPermit: (meta as any)?.workPermit ?? (meta as any)?.work_permit ?? undefined,
              scheduledAt: (meta as any)?.scheduledAt ?? (meta as any)?.scheduled_at ?? undefined,
              assets: (meta as any)?.assets ?? undefined,
              type: (meta as any)?.type ?? undefined,
              source: (meta as any)?.source ?? undefined,
              priority: ticket.severity,
              subject: (meta as any)?.subject ?? undefined,
              status: ticket.status,
            }}
            />
          </Box>
        </Box>

        {/* ================= RIGHT ================= */}
        <Box
          w="340px"
          flexShrink={0}
          display="flex"
          flexDir="column"
          gap={4}
          minH={0}
          h={propertiesHeight != null ? `${propertiesHeight}px` : undefined}
          overflow="hidden"
        >
          <StatusSlaCard
            statusLabel={statusLabel}
            slaDueAt={ticket.slaDueAt ?? null}
            statusValue={form.status}
            statusOptions={statusOptions}
            onChangeStatus={(value) => {
              const prevLabel = statusOptions.find((o) => o.value === form.status)?.label || form.status
              const newLabel = statusOptions.find((o) => o.value === value)?.label || value
              setField('status', value as any)
              apiClient.patch(`/tickets/${id}`, { status: value })
                .then(() => {
                  showSuccess('Status updated')
                  TicketService.createTicketUpdate(id!, {
                    content: `Status changed from "${prevLabel}" to "${newLabel}"`,
                    type: 'status_change',
                    is_internal: true,
                  }).then(() => fetchTicketUpdates())
                })
                .catch((err) => showError(err))
            }}
            isReadOnly={false}
            hidePriority={true}
            onAddComment={onUpdateOpen}
          />

          <Box flex="1" minH={0} overflow="hidden" display="flex" flexDir="column">
            <Timeline items={timelineItems} h="100%" minH={0} />
          </Box>
        </Box>
      </Flex>

      {/* Ticket Updates Modal */}
      <TicketUpdateModal
        isOpen={isUpdateOpen || editingUpdate !== null}
        onClose={() => {
          onUpdateClose()
          setEditingUpdate(null)
        }}
        ticketId={id!}
        ticketNumber={(meta as any)?.ticket_number}
        onSubmitted={fetchTicketUpdates}
        editingUpdate={editingUpdate}
      />
    </Box>
  )
}
